<template>
  <div class="inv-sheet">
    <h1 class="inv-title">SERVICE INVOICE</h1>

    <div class="inv-head">
      <div class="inv-seller">
        <p class="inv-seller__name">{{ seller.name }}</p>
        <p v-if="seller.address_line1" class="inv-line">
          {{ seller.address_line1 }}
        </p>
        <p v-if="seller.address_line2" class="inv-line">
          {{ seller.address_line2 }}
        </p>
        <p v-if="sellerCityLine" class="inv-line">{{ sellerCityLine }}</p>
        <p v-if="seller.gstin" class="inv-line inv-line--strong">
          GSTIN: {{ seller.gstin }}
        </p>
        <p v-if="seller.pan" class="inv-line inv-line--strong">
          PAN: {{ seller.pan }}
        </p>
      </div>

      <div class="inv-meta">
        <div class="inv-meta__row">
          <span>Inv No</span><strong>{{ document.invoice_number }}</strong>
        </div>
        <div class="inv-meta__row">
          <span>Inv Date</span
          ><strong>{{ formatDate(document.invoice_date) }}</strong>
        </div>
        <div v-if="document.status === 'cancelled'" class="inv-cancelled">
          CANCELLED
        </div>
      </div>
    </div>

    <div class="inv-buyer">
      <p class="inv-buyer__label">Bill To:</p>
      <p class="inv-buyer__name">{{ buyer.name }}</p>
      <p v-if="buyer.address_line1" class="inv-line">
        {{ buyer.address_line1 }}
      </p>
      <p v-if="buyer.address_line2" class="inv-line">
        {{ buyer.address_line2 }}
      </p>
      <p v-if="buyerCityLine" class="inv-line">{{ buyerCityLine }}</p>
      <p v-if="buyer.gstin" class="inv-line inv-line--strong">
        GSTIN: {{ buyer.gstin }}
      </p>
    </div>

    <table class="inv-table">
      <thead>
        <tr>
          <th class="inv-th inv-th--sno">S.No</th>
          <th class="inv-th">Item &amp; Description</th>
          <th class="inv-th inv-th--hsn">HSN/SAC</th>
          <th class="inv-th inv-th--qty">Qty</th>
          <th class="inv-th inv-th--num">Rate</th>
          <th class="inv-th inv-th--num">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="inv-td inv-td--sno">1</td>
          <td class="inv-td">
            {{ document.description || document.category_name }}
          </td>
          <td class="inv-td inv-td--hsn">{{ document.hsn }}</td>
          <td class="inv-td inv-td--qty">1</td>
          <td class="inv-td inv-td--num">{{ money(document.net_amount) }}</td>
          <td class="inv-td inv-td--num">{{ money(document.net_amount) }}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td class="inv-tf" colspan="3"></td>
          <td class="inv-tf inv-tf--label" colspan="2">Sub Total</td>
          <td class="inv-tf inv-tf--num">{{ money(document.net_amount) }}</td>
        </tr>
        <tr>
          <td class="inv-tf" colspan="3"></td>
          <td class="inv-tf inv-tf--label" colspan="2">CGST {{ halfRate }}%</td>
          <td class="inv-tf inv-tf--num">{{ money(document.cgst_amount) }}</td>
        </tr>
        <tr>
          <td class="inv-tf" colspan="3"></td>
          <td class="inv-tf inv-tf--label" colspan="2">SGST {{ halfRate }}%</td>
          <td class="inv-tf inv-tf--num">{{ money(document.sgst_amount) }}</td>
        </tr>
        <tr class="inv-grand">
          <td class="inv-tf" colspan="3"></td>
          <td class="inv-tf inv-tf--label" colspan="2">Grand Total</td>
          <td class="inv-tf inv-tf--num">{{ money(document.total_amount) }}</td>
        </tr>
      </tfoot>
    </table>

    <p class="inv-words">(In Words : {{ document.amount_in_words }})</p>

    <div v-if="showWorking && period" class="inv-basis">
      <span class="inv-basis__label">Worked out on</span>
      <span>
        {{ money(document.base_amount) }} collected during {{ period.label
        }}{{ period.batch_label ? ` · ${period.batch_label}` : "" }}
        <template v-if="period.student_count">
          ({{ period.student_count }} students)
        </template>
        at {{ number(document.rate) }}%
      </span>
    </div>

    <div v-if="bank.bank_name" class="inv-bank">
      <p class="inv-bank__title">Bank Details:</p>
      <p class="inv-line">Bank Name: {{ bank.bank_name }}</p>
      <p v-if="bank.account_type" class="inv-line">
        Acc Type&nbsp;&nbsp;&nbsp;&nbsp;: {{ bank.account_type }}
      </p>
      <p v-if="bank.account_number" class="inv-line">
        Acc No&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;: {{ bank.account_number }}
      </p>
      <p v-if="bank.ifsc" class="inv-line">
        IFSC Code&nbsp;&nbsp;: {{ bank.ifsc }}
      </p>
    </div>

    <div class="inv-sign">
      <p class="inv-sign__for">For {{ seller.name }},</p>
      <div class="inv-sign__space"></div>
      <p class="inv-sign__name">
        {{ seller.signatory_name || "Authority Signatory" }}
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: "ServiceInvoicePrint",

  props: {
    document: { type: Object, required: true },
    showWorking: { type: Boolean, default: false },
  },

  computed: {
    seller() {
      return this.document.seller || {};
    },

    buyer() {
      return this.document.buyer || {};
    },

    bank() {
      return this.document.bank || {};
    },

    period() {
      return this.document.period || null;
    },

    halfRate() {
      return this.number(Number(this.document.gst_percentage || 0) / 2);
    },

    sellerCityLine() {
      return this.addressLine(this.seller);
    },

    buyerCityLine() {
      return this.addressLine(this.buyer);
    },
  },

  methods: {
    addressLine(party) {
      const city = [party.city, party.state].filter(Boolean).join(", ");
      return [city, party.pincode].filter(Boolean).join(" - ");
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    number(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      });
    },

    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}/${date.getFullYear()}`;
    },
  },
};
</script>

<style scoped>
.inv-sheet {
  width: 720px;
  margin: 0 auto;
  padding: 32px 36px;
  background: #fff;
  color: #101828;
  font-size: 12.5px;
  line-height: 1.5;
}

.inv-title {
  margin: 0 0 24px;
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
  text-decoration: underline;
}

.inv-head {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.inv-seller__name {
  margin: 0;
  font-weight: 700;
  font-size: 13.5px;
  text-transform: uppercase;
}

.inv-line {
  margin: 0;
}

.inv-line--strong {
  font-weight: 600;
}

.inv-meta {
  min-width: 210px;
}

.inv-meta__row {
  display: flex;
  gap: 8px;
}

.inv-meta__row span {
  min-width: 62px;
}

.inv-meta__row span::after {
  content: ":";
  float: right;
}

.inv-cancelled {
  margin-top: 8px;
  padding: 2px 8px;
  display: inline-block;
  border: 1px solid #d92d20;
  color: #d92d20;
  font-weight: 700;
  letter-spacing: 1px;
}

.inv-buyer {
  margin-top: 22px;
}

.inv-buyer__label {
  margin: 0 0 2px;
  font-weight: 700;
}

.inv-buyer__name {
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
}

.inv-table {
  width: 100%;
  margin-top: 22px;
  border-collapse: collapse;
}

.inv-th {
  padding: 7px 8px;
  text-align: left;
  font-size: 11.5px;
  font-weight: 700;
  border: 1px solid #101828;
  background: #f2f4f7;
}

.inv-th--sno,
.inv-td--sno {
  width: 42px;
  text-align: center;
}

.inv-th--hsn,
.inv-td--hsn {
  width: 78px;
  text-align: center;
}

.inv-th--qty,
.inv-td--qty {
  width: 46px;
  text-align: center;
}

.inv-th--num {
  text-align: right;
  width: 110px;
}

.inv-td {
  padding: 10px 8px;
  border: 1px solid #101828;
  vertical-align: top;
}

.inv-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.inv-tf {
  padding: 6px 8px;
  border: 1px solid #101828;
  border-top: none;
}

.inv-tf--label {
  font-weight: 600;
}

.inv-tf--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.inv-grand .inv-tf {
  font-weight: 700;
  background: #f2f4f7;
}

.inv-words {
  margin: 10px 0 0;
  font-style: italic;
}

.inv-basis {
  margin-top: 14px;
  padding: 8px 10px;
  border-left: 3px solid #98a2b3;
  background: #f9fafb;
  font-size: 11.5px;
  color: #475467;
}

.inv-basis__label {
  display: block;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 10px;
}

.inv-bank {
  margin-top: 26px;
}

.inv-bank__title {
  margin: 0 0 2px;
  font-weight: 700;
}

.inv-sign {
  margin-top: 30px;
}

.inv-sign__for {
  margin: 0;
  font-weight: 600;
}

.inv-sign__space {
  height: 52px;
}

.inv-sign__name {
  margin: 0;
  font-weight: 600;
}
</style>
